/**
 * journeyDoc — client subscription to a Co-Drive journey document
 * (docs/FRAMEWORK-computer-use-co-drive.md; Phase 0: journey type "signin").
 *
 * The browser-service on the account VM is the SINGLE writer of
 * accounts/{accountId}/journeys/{jid}; account members have read access per
 * firestore.rules. The client renders chrome from this doc — in Phase 0 only
 * the persistent pause control. Silent-degrade error handling (the
 * VmLifecycleBanner idiom): any rules/transport error just reports null, so
 * a missing rule or an offline doc can never break the sign-in surface.
 */

import { getApp } from 'firebase/app';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { ACCOUNT_FIRESTORE_PREFIX } from '../prest0nRuntime/constants';

/** The journey-doc fields the Phase-0 client reads (all written by the VM). */
export interface JourneyDocSnapshot {
  protocolVersion?: string;
  type?: string;
  app?: string;
  control?: string;
  wall?: string | null;
  verdict?: Record<string, unknown> | null;
  closedReason?: string | null;
  /** Where the drive last landed, as the workstation classified it
   *  (contract C4, 2026-09-02) — the same receipt the server reads when a
   *  destination report asks for a strike. READ-ONLY here, and absent on
   *  every VM build that predates it: nothing client-side may write it, and
   *  no chrome may depend on its presence. */
  lastLanding?: {
    url?: string;
    kind?: string;
    status?: number;
    title?: string;
    at?: number;
  } | null;
}

/**
 * Subscribe to a journey doc. cb(null) on missing doc or any error.
 * Returns the unsubscribe function.
 */
export function subscribeToJourney(
  accountId: string,
  journeyId: string,
  cb: (snapshot: JourneyDocSnapshot | null) => void
): () => void {
  try {
    const db = getFirestore(getApp());
    return onSnapshot(
      doc(db, ACCOUNT_FIRESTORE_PREFIX, accountId, 'journeys', journeyId),
      (snap) => cb(snap.exists() ? (snap.data() as JourneyDocSnapshot) : null),
      () => cb(null)
    );
  } catch {
    cb(null);
    return () => undefined;
  }
}
